import { createFileRoute, Link } from "@tanstack/react-router";
import { ScoringRubric } from "@/components/assessment/ScoringRubric";
import { NamedTargets } from "@/components/assessment/NamedTargets";
import { motion } from "framer-motion";
import { useState } from "react";

const statusItems = [
  { label: "Market Reach", detail: "TAM calculator", live: true },
  { label: "Scoring", detail: "11 weighted criteria", live: true },
  { label: "Named Targets", detail: "tracker", live: true },
];

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  }).format(value);

const formatPercent = (value: number) =>
  new Intl.NumberFormat("en-US", {
    style: "percent",
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  }).format(value);

export const Route = createFileRoute("/assessment")({
  head: () => ({
    meta: [
      { title: "Growth Engine Assessment | Katie Fernands" },
      {
        name: "description",
        content:
          "A scored, deep-dive growth assessment for RIAs and financial advisors. Market reach, source strength, and named targets in one place.",
      },
      { name: "robots", content: "noindex" },
      { property: "og:title", content: "Growth Engine Assessment | Katie Fernands" },
      {
        property: "og:description",
        content:
          "A scored, deep-dive growth assessment for RIAs and financial advisors. Market reach, source strength, and named targets in one place.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://katiefernands.com/assessment" }],
  }),
  component: AssessmentPage,
});

function AssessmentPage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Minimal brand header — same top-left treatment as the homepage, without navigation links */}
      <header className="fixed top-0 left-0 right-0 z-50 py-6">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Link
            to="/"
            className="inline-flex items-baseline gap-2 font-serif text-lg tracking-tight text-foreground"
          >
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-primary translate-y-[-2px]" />
            Katie Fernands
          </Link>
        </div>
      </header>

      <main>
        <div className="relative mx-auto w-full max-w-7xl px-6 lg:px-10 pt-32 pb-8">
          {/* Soft background accent — same palette as the homepage hero */}
          <div
            aria-hidden
            className="absolute inset-0 pointer-events-none opacity-40"
            style={{
              background:
                "radial-gradient(ellipse 50% 50% at 80% 30%, oklch(0.63 0.035 130 / 0.12), transparent 65%)",
            }}
          />

          <div className="relative max-w-2xl">
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.05 }}
              className="mb-6 text-xs font-medium uppercase tracking-[0.18em] text-sage"
            >
              Growth Engine Assessment
            </motion.p>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.12 }}
              className="font-serif text-5xl sm:text-6xl md:text-7xl font-normal tracking-tight leading-[0.98]"
            >
              Building this live.
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.24 }}
              className="mt-6 max-w-xl text-lg leading-relaxed text-muted-foreground"
            >
              This is the scored, deep-dive version of the Scan — market reach, source
              strength, and named targets, all in one place. I'm building it in public
              this week.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.36 }}
              className="mt-12 border-l border-primary/40 pl-6"
            >
              <p className="mb-4 text-sm font-medium uppercase tracking-[0.14em] text-foreground/80">
                Status
              </p>
              <ul className="space-y-4">
                {statusItems.map((item) => (
                  <li
                    key={item.label}
                    className="flex items-center justify-between gap-4 border-b border-border pb-4 last:border-0"
                  >
                    <div>
                      <span className="font-serif text-lg">{item.label}</span>
                      <span className="ml-2 text-sm text-muted-foreground">
                        — {item.detail}
                      </span>
                    </div>
                    {item.live ? (
                      <span className="inline-flex items-center gap-1.5 shrink-0 text-xs font-medium uppercase tracking-wider text-primary">
                        <span className="inline-block h-1.5 w-1.5 rounded-full bg-primary" />
                        Live
                      </span>
                    ) : (
                      <span className="shrink-0 text-xs font-medium uppercase tracking-wider text-sage">
                        In progress
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </motion.div>

          </div>
        </div>

        <div className="relative mx-auto w-full max-w-5xl px-6 lg:px-10 pb-32">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.48 }}
          >
            <TamCalculator />
            <ScoringRubric />
            <NamedTargets />
          </motion.div>
        </div>
      </main>
    </div>
  );
}

function TamCalculator() {
  const [niche, setNiche] = useState("");
  const [geography, setGeography] = useState("");
  const [households, setHouseholds] = useState("");
  const [averageAssets, setAverageAssets] = useState("");
  const [captureRate, setCaptureRate] = useState("");
  const [currentAum, setCurrentAum] = useState("");

  const numHouseholds = Math.max(0, parseFloat(households) || 0);
  const numAverageAssets = Math.max(0, parseFloat(averageAssets) || 0);
  const numCaptureRate = Math.max(0, Math.min(100, parseFloat(captureRate) || 0)) / 100;
  const numCurrentAum = Math.max(0, parseFloat(currentAum) || 0);

  const tam = numHouseholds * numAverageAssets;
  const realisticCaptureTarget = tam * numCaptureRate;
  const penetrationRate = tam > 0 ? numCurrentAum / tam : 0;
  const growthHeadroom = realisticCaptureTarget - numCurrentAum;

  return (
    <section className="rounded-xl border border-border bg-card/60 p-6 sm:p-8 backdrop-blur-sm">
      <div className="mb-8">
        <p className="mb-2 text-xs font-medium uppercase tracking-[0.18em] text-sage">
          Market Reach
        </p>
        <h2 className="font-serif text-3xl font-normal tracking-tight text-foreground">
          Total Addressable Market
        </h2>
        <p className="mt-2 text-base text-muted-foreground">
          Size your niche live, in front of the room.
        </p>
      </div>

      <div className="grid gap-6 sm:grid-cols-2">
        <div className="space-y-2">
          <label htmlFor="niche" className="text-sm font-medium text-foreground/80">
            Niche description
          </label>
          <input
            id="niche"
            type="text"
            value={niche}
            onChange={(e) => setNiche(e.target.value)}
            placeholder="Retiring dentists"
            className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-foreground placeholder:text-muted-foreground/60 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>

        <div className="space-y-2">
          <label htmlFor="geography" className="text-sm font-medium text-foreground/80">
            Geography
          </label>
          <input
            id="geography"
            type="text"
            value={geography}
            onChange={(e) => setGeography(e.target.value)}
            placeholder="Twin Cities metro"
            className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-foreground placeholder:text-muted-foreground/60 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>

        <div className="space-y-2">
          <label htmlFor="households" className="text-sm font-medium text-foreground/80">
            Qualifying households or businesses
          </label>
          <input
            id="households"
            type="number"
            min="0"
            step="1"
            value={households}
            onChange={(e) => setHouseholds(e.target.value)}
            placeholder="1,200"
            className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-foreground placeholder:text-muted-foreground/60 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>

        <div className="space-y-2">
          <label htmlFor="averageAssets" className="text-sm font-medium text-foreground/80">
            Average investable assets
          </label>
          <input
            id="averageAssets"
            type="number"
            min="0"
            step="1"
            value={averageAssets}
            onChange={(e) => setAverageAssets(e.target.value)}
            placeholder="2500000"
            className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-foreground placeholder:text-muted-foreground/60 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>

        <div className="space-y-2">
          <label htmlFor="captureRate" className="text-sm font-medium text-foreground/80">
            Target capture rate (%)
          </label>
          <input
            id="captureRate"
            type="number"
            min="0"
            max="100"
            step="0.1"
            value={captureRate}
            onChange={(e) => setCaptureRate(e.target.value)}
            placeholder="10"
            className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-foreground placeholder:text-muted-foreground/60 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>

        <div className="space-y-2">
          <label htmlFor="currentAum" className="text-sm font-medium text-foreground/80">
            Current AUM already captured from this niche
          </label>
          <input
            id="currentAum"
            type="number"
            min="0"
            step="1"
            value={currentAum}
            onChange={(e) => setCurrentAum(e.target.value)}
            placeholder="50000000"
            className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-foreground placeholder:text-muted-foreground/60 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>
      </div>

      <div className="mt-10 grid gap-4 sm:grid-cols-2">
        <OutputCard
          label="Total Addressable Market"
          value={formatCurrency(tam)}
          note="households × average assets"
        />
        <OutputCard
          label="Realistic Capture Target"
          value={formatCurrency(realisticCaptureTarget)}
          note="TAM × capture rate"
        />
        <OutputCard
          label="Current Penetration Rate"
          value={formatPercent(penetrationRate)}
          note="current AUM ÷ TAM"
        />
        <OutputCard
          label="Growth Headroom"
          value={formatCurrency(growthHeadroom)}
          note="capture target − current AUM"
        />
      </div>
    </section>
  );
}

function OutputCard({
  label,
  value,
  note,
}: {
  label: string;
  value: string;
  note: string;
}) {
  return (
    <div className="rounded-lg border border-primary/20 bg-background p-5">
      <p className="text-xs font-medium uppercase tracking-[0.14em] text-muted-foreground">
        {label}
      </p>
      <p className="mt-2 font-serif text-3xl sm:text-4xl font-normal tracking-tight text-primary">
        {value}
      </p>
      <p className="mt-1 text-sm text-muted-foreground">{note}</p>
    </div>
  );
}

